/**
 * Own-hold token — the invoice id of a PYN hold the current visitor created but
 * has not yet paid for (`added_pending_confirm`). It is the browser's proof that
 * a room the site shows as unavailable is unavailable *because of this visitor*,
 * so we can (a) exclude it from their own checkout availability check and
 * (b) offer them a reclaim / self-release affordance on the public site instead
 * of leaving them locked out of their own room (the "Catherine" case, 2026-07).
 *
 * Why localStorage (not sessionStorage): the previous implementation kept this
 * in sessionStorage, which is per-tab and dies the instant the guest closes the
 * tab. A guest who submits, bounces off the gateway, then closes the tab and
 * comes back later (a different tab, or after a browser restart) had no token —
 * and saw their own hold as a hard block. localStorage survives tab close and
 * browser restart within the same browser, closing that gap. A TTL envelope
 * keeps a stale token from lingering for days; the server re-validates the token
 * on every use anyway (retry-data serves only non-completed bookings, /release
 * only cancels a still-pending hold), so an expired-but-present token is at
 * worst a no-op, never a way to touch a paid booking.
 *
 * Client-only: every function is a no-op under SSR (`typeof window`), so this
 * never runs on the server and never touches the SSR/SEO render path.
 */

export const OWN_HOLD_INVOICE_KEY = 'checkout:ownHoldInvoice';

/** How long a stored token is trusted before it self-expires. A hold older than
 *  this has long since been swept; the server validates regardless. */
export const OWN_HOLD_TTL_MS = 12 * 60 * 60 * 1000;

interface OwnHoldEnvelope {
  invoiceId: string;
  storedAt: number;
}

/** Persist the invoice id of the hold this browser just created. */
export function setOwnHold(invoiceId: string): void {
  if (typeof window === 'undefined' || !invoiceId) return;
  try {
    const env: OwnHoldEnvelope = { invoiceId, storedAt: Date.now() };
    window.localStorage.setItem(OWN_HOLD_INVOICE_KEY, JSON.stringify(env));
  } catch {
    /* localStorage unavailable (private mode / quota) — non-fatal */
  }
}

/** Read the current own-hold invoice id, or null if absent/expired/corrupt. */
export function getOwnHold(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(OWN_HOLD_INVOICE_KEY);
    if (!raw) return null;
    const env = JSON.parse(raw) as OwnHoldEnvelope;
    if (!env || typeof env.invoiceId !== 'string' || typeof env.storedAt !== 'number') {
      window.localStorage.removeItem(OWN_HOLD_INVOICE_KEY);
      return null;
    }
    if (Date.now() - env.storedAt > OWN_HOLD_TTL_MS) {
      window.localStorage.removeItem(OWN_HOLD_INVOICE_KEY);
      return null;
    }
    return env.invoiceId;
  } catch {
    try { window.localStorage.removeItem(OWN_HOLD_INVOICE_KEY); } catch { /* ignore */ }
    return null;
  }
}

/** Forget the own-hold token (booking completed, released, or no longer valid). */
export function clearOwnHold(): void {
  if (typeof window === 'undefined') return;
  try { window.localStorage.removeItem(OWN_HOLD_INVOICE_KEY); }
  catch { /* localStorage unavailable — non-fatal */ }
}
